import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from "@sentry/react";
import { AuthProvider } from './contexts/AuthContext';
import { BillingProvider } from './contexts/BillingContext';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorFallback from './components/common/ErrorFallback';
import { ProtectedRoute } from './components/ProtectedRoute';

// Layouts
// Components
import AdminGuard from './components/AdminGuard';
import DashboardLayout from './components/dashboard/DashboardLayout';

// Pages - Critical Path
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
const AdminSettingsPage = React.lazy(() => import('./pages/admin/AdminSettingsPage')); // Added lazy load logic
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));

// Pages - Lazy Loaded (Code Splitting)
const FeaturesPage = React.lazy(() => import('./pages/FeaturesPage'));
const TargetAudiencePage = React.lazy(() => import('./pages/TargetAudiencePage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const FAQPage = React.lazy(() => import('./pages/FAQPage'));
const TestimonialsPage = React.lazy(() => import('./pages/TestimonialsPage'));
const BookingPage = React.lazy(() => import('./pages/BookingPage'));
const TenantSelectionPage = React.lazy(() => import('./pages/TenantSelectionPage'));
const SchedulerPage = React.lazy(() => import('./pages/SchedulerPage'));
const CreateEstablishmentPage = React.lazy(() => import('./pages/CreateEstablishmentPage'));
const ClubPage = React.lazy(() => import('./pages/ClubPage'));
const ReviewPage = React.lazy(() => import('./pages/public/ReviewPage'));
const AppointmentDetailsPage = React.lazy(() => import('./pages/AppointmentDetailsPage'));
const ClientPortalPage = React.lazy(() => import('./pages/ClientPortalPage'));
const TermsPage = React.lazy(() => import('./pages/TermsPage'));
const PrivacyPage = React.lazy(() => import('./pages/PrivacyPage'));
const BusinessLandingPage = React.lazy(() => import('./pages/BusinessLandingPage'));

// Dashboard Pages - Lazy Loaded
const OverviewPage = React.lazy(() => import('./pages/dashboard/OverviewPage'));
const AgendaPage = React.lazy(() => import('./pages/dashboard/AgendaPage'));
const FinancialPage = React.lazy(() => import('./pages/dashboard/FinancialPage'));
const CommissionsPage = React.lazy(() => import('./pages/dashboard/CommissionsPage'));
const ServiceValuesPage = React.lazy(() => import('./pages/dashboard/ServiceValuesPage'));
const ClientsPage = React.lazy(() => import('./pages/dashboard/ClientsPage'));
const ServicesPage = React.lazy(() => import('./pages/dashboard/ServicesPage'));
const ReportsPage = React.lazy(() => import('./pages/dashboard/ReportsPage'));
const MarketingPage = React.lazy(() => import('./pages/dashboard/MarketingPage'));
const SettingsPage = React.lazy(() => import('./pages/dashboard/SettingsPage'));
const SubscriptionPage = React.lazy(() => import('./pages/dashboard/SubscriptionPage'));
const TeamPage = React.lazy(() => import('./pages/dashboard/TeamPage'));
const ProfessionalsPage = React.lazy(() => import('./pages/admin/Professionals'));
const ProductsPage = React.lazy(() => import('./pages/dashboard/ProductsPage'));
const ProductSalesReportPage = React.lazy(() => import('./pages/dashboard/ProductSalesReportPage'));
const AppointmentsReportPage = React.lazy(() => import('./pages/dashboard/AppointmentsReportPage'));
const FinancialReportPage = React.lazy(() => import('./pages/dashboard/FinancialReportPage'));
const MembershipPage = React.lazy(() => import('./pages/dashboard/MembershipPage'));
const ReviewsPage = React.lazy(() => import('./pages/dashboard/ReviewsPage'));
const BookingSitePage = React.lazy(() => import('./pages/dashboard/BookingSitePage'));
const WaitlistPage = React.lazy(() => import('./pages/dashboard/WaitlistPage'));

// Admin Pages
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTenantsPage = React.lazy(() => import('./pages/admin/AdminTenantsPage'));
const AdminBillingPage = React.lazy(() => import('./pages/admin/AdminBillingPage'));

// Microsite Pages - Lazy Loaded
const BookSalonPage = React.lazy(() => import('./book/pages/BookSalonPage'));
const BookServicesPage = React.lazy(() => import('./book/pages/BookServicesPage'));
const BookSchedulePage = React.lazy(() => import('./book/pages/BookSchedulePage'));
import { TenantProvider } from './contexts/TenantContext';
import TenantGuard from './components/TenantGuard';

const App: React.FC = () => {
  return (
    <Sentry.ErrorBoundary fallback={({ error, resetError }) => <ErrorFallback error={error as Error} resetErrorBoundary={resetError} />}>
      <AuthProvider>
        <BillingProvider>
          <TenantProvider>
            <BrowserRouter>
              <div className="min-h-screen bg-background-light dark:bg-background-dark font-sans text-text-main">
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/business" element={<BusinessLandingPage />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/target-audience" element={<TargetAudiencePage />} />
                    <Route path="/pricing" element={<PricingPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/testimonials" element={<TestimonialsPage />} />
                    <Route path="/booking" element={<BookingPage />} />
                    <Route path="/book/:slug" element={<BookingPage />} />
                    <Route path="/club" element={<ClubPage />} />
                    <Route path="/avalie/:token" element={<ReviewPage />} />
                    <Route path="/minha-area/:slug" element={<ClientPortalPage />} />
                    <Route path="/appointments/:id" element={<AppointmentDetailsPage />} />

                    {/* Auth Routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />

                    {/* AI Scheduler Demo Page */}
                    <Route path="/scheduler" element={<SchedulerPage />} />

                    {/* Protected Routes (Auth Only) */}
                    <Route element={<ProtectedRoute />}>
                      <Route path="/select-tenant" element={<TenantSelectionPage />} />
                      <Route path="/create-establishment" element={<CreateEstablishmentPage />} />

                      {/* Tenant Required Routes (Dashboard) */}
                      <Route element={<TenantGuard />}>
                        <Route path="/dashboard" element={<DashboardLayout />}>
                          <Route index element={<OverviewPage />} />
                          <Route path="agenda" element={<AgendaPage />} />
                          <Route path="clients" element={<ClientsPage />} />
                          <Route path="services" element={<ServicesPage />} />
                          <Route path="team" element={<TeamPage />} />
                          <Route path="financial" element={<FinancialPage />} />
                          <Route path="financial/commissions" element={<CommissionsPage />} />
                          <Route path="financial/values" element={<ServiceValuesPage />} />
                          <Route path="marketing" element={<MarketingPage />} />
                          <Route path="membership" element={<MembershipPage />} />
                          <Route path="reports" element={<ReportsPage />} />
                          <Route path="settings" element={<SettingsPage />} />
                          <Route path="subscription" element={<SubscriptionPage />} />
                          <Route path="products" element={<ProductsPage />} />
                          <Route path="reports/product-sales" element={<ProductSalesReportPage />} />
                          <Route path="reports/appointments" element={<AppointmentsReportPage />} />
                          <Route path="reports/appointments" element={<AppointmentsReportPage />} />
                          <Route path="reports/financial" element={<FinancialReportPage />} />
                          <Route path="reviews" element={<ReviewsPage />} />
                          <Route path="booking-site" element={<BookingSitePage />} />
                          <Route path="waitlist" element={<WaitlistPage />} />
                        </Route>

                        {/* Explicit Top-Level Routes (Aliases) */}
                        <Route path="/clients" element={<DashboardLayout><ClientsPage /></DashboardLayout>} />
                        <Route path="/staff" element={<DashboardLayout><ProfessionalsPage /></DashboardLayout>} />
                      </Route>
                    </Route>

                    {/* Admin Routes (Super Admin) */}
                    <Route element={<AdminGuard />}>
                      <Route path="/admin" element={<AdminLayout />}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="tenants" element={<AdminTenantsPage />} />
                        <Route path="billing" element={<AdminBillingPage />} />
                        <Route path="settings" element={<AdminSettingsPage />} />
                      </Route>
                    </Route>

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </div>
            </BrowserRouter>
          </TenantProvider>
        </BillingProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
